import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import "dayjs/locale/fr";

import useItem from "common/hooks/useItem";
import ImageFromStrapiMedia from "common/components/ImageFromStrapiMedia/ImageFromStrapiMedia";
import useRouter from "common/hooks/use-router";
import DetailHeader from "common/components/DetailHeader/DetailHeader";
import SectionTitle from "common/components/SectionTitle/SectionTitle";
import PageLoading from "common/components/PageLoading/PageLoading";
import ImageComponent from "common/components/ImageComponent/ImageComponent";
import Status from "common/components/Status/Status";
import AlertsContext from "common/providers/alerts";
import { Conversation } from "types/Conversation";
import formatPrice from "utils/formatPrice";
import ProfileCard from "components/ProfileCard/ProfileCard";
import Icon from "components/Icon/Icon";
import HeaderButtons from "common/components/DetailHeader/HeaderButtons";
import leadZeros from "utils/leadZeros";

function ConversationDetails() {
  const router = useRouter();
  const id = router.query.id as string;
  const { t } = useTranslation();
  const { setAlert } = useContext(AlertsContext);
  const [errorMessage] = useState("");

  const { item, isFetching, error }: any = useItem<Conversation>(
    "conversations",
    id,
    {
      populate: [
        "content",
        "content.category",
        "content.category.logo",
        "providerProfile",
        "providerProfile.avatar",
        "providerProfile.companyLogo",
        "applicantProfile",
        "applicantProfile.avatar",
        "applicantProfile.companyLogo",
        "content.images",
      ],
    }
  );

  function setFullScreenImg(_image: string) {
    throw new Error("Function not implemented.");
  }

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    } else if (errorMessage) {
      setAlert(errorMessage, "danger");
    }
  }, [error, errorMessage, setAlert]);

  const {
    content,
    providerProfile,
    applicantProfile,
    status,
    // blocked,
    // finalExchangeOffer,
    finalPrice,
    updatedAt,
  } = (item as Conversation) || {};

  const HeaderSubTitle = useCallback(
    (mainText: string, subText?: string) => (
      <>
        <span className="font-bold">{mainText}</span>
        {subText && <span> | {subText}</span>}
      </>
    ),
    []
  );

  const ConversationHeaderInfos = useMemo(
    () => (
      <div className="w-full">
        <div className="flex items-center text-xs gap-1 field-title">
          <Icon name="ClockIcon" className="w-4 h-4" />
          {dayjs(updatedAt).fromNow()}
        </div>
      </div>
    ),
    [updatedAt]
  );

  if (isFetching || !item) {
    return <PageLoading />;
  }
  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
      <DetailHeader
        backLink="/conversations"
        title={t("titleLegends.conversation", { id: leadZeros(id) })}
        Status={
          <div className="flex gap-2 flex-wrap">
            <Status status={status} />
          </div>
        }
        TitleIcon={
          <div className="hidden lg:flex items-center justify-center border border-primary w-14 min-w-[3.5rem] h-14 rounded-lg">
            <Icon name="BiConversation" className="text-primary w-7 h-7" />
          </div>
        }
        SubTitle={
          content ? (
            HeaderSubTitle(content?.category?.name)
          ) : (
            <>Annonce supprimée</>
          )
        }
        HeaderInfos={ConversationHeaderInfos}
        HeaderButtons={<HeaderButtons editUrl={`/conversations/edit/${id}`} />}
      />

      <div className="responsive-flex gap-2 mt-2">
        <div className="white-box responsive-flex gap-4 flex-col w-full lg:basis-1/2">
          <ProfileCard profile={providerProfile} title={t("forms.buyer")} />
        </div>

        <div className="white-box responsive-flex gap-4 flex-col w-full lg:basis-1/2">
          <ProfileCard profile={applicantProfile} title={t("forms.seller")} />
        </div>
      </div>
      <div className="responsive-flex gap-2 mt-2">
        <div className="flex-1">
          <div className="flex flex-col gap-2 w-full">
            {/* Description */}
            <div className="responsive-flex flex flex-col lg:flex-row gap-4 items-start">
              {/* Column 1 */}
              <div className="white-box responsive-flex gap-2 flex-col w-full">
                <div className="flex flex-row gap-4">
                  <div className="basis-1/3">
                    <SectionTitle
                      title={t("forms.title")}
                      icon="WalletIcon"
                      className="text-secondary mt-2"
                    />
                    <div className="responsive-flex flex-row">
                      <span className="font-bold text-xl py-1 pl-4">
                        {content?.title}
                      </span>
                    </div>
                  </div>
                  <div className="basis-1/3">
                    <SectionTitle
                      title={t("forms.budget")}
                      icon="WalletIcon"
                      className="text-secondary mt-2"
                    />
                    <div className="responsive-flex flex-row">
                      <span className="font-bold text-xl py-1 pl-4">
                        {content?.price
                          ? formatPrice(content?.price)
                          : "à convenir"}
                      </span>
                    </div>
                  </div>
                </div>
                <SectionTitle
                  title={t("forms.description")}
                  icon="BookmarkIcon"
                  className="text-secondary mt-2"
                />
                <div className="responsive-flex py-2 px-4 flex-col border rounded">
                  {content?.description ? (
                    content.description
                      .split("\n")
                      .map((p: any, index: number) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <p key={`p-${index}`} className="mb-4">
                          {p}
                        </p>
                      ))
                  ) : (
                    <p>Annonce supprimée</p>
                  )}
                </div>
              </div>
            </div>
            {/* Images */}
            <div className="responsive-flex white-box">
              <div className="responsive-flex gap-4 flex-col w-full">
                <div className="responsive-flex gap-4 w-full flex-row flex-wrap justify-center">
                  {content?.images?.map((image, index) => (
                    <div
                      // eslint-disable-next-line react/no-array-index-key
                      key={`image-${index}`}
                      className="rounded-lg overflow-hidden aspect-square h-[250px] md:h-[180px]"
                      onClick={() => {
                        setFullScreenImg(image);
                      }}
                    >
                      <ImageComponent
                        image={ImageFromStrapiMedia(image)?.uri}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConversationDetails;
